import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from './AuthContext';

export const ChatContext = createContext();

const URL_ASISTENTE_IA = process.env.REACT_APP_URL_ASISTENTE_IA;
const authHeaders = () => ({ headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } });

export const ChatContextProvider = ({ children }) => {
    const { user } = useContext(AuthContext);

    // Sólo UI (no es dato de negocio) — se mantiene en localStorage por conveniencia.
    const [isOpen, setIsOpen] = useState(() => {
        try {
            return localStorage.getItem('chat_isOpen') === 'true';
        } catch (e) {
            return false;
        }
    });

    const [conversaciones, setConversaciones] = useState([]);
    const [activeConv, setActiveConv] = useState(null); // { _id, titulo }
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingConv, setLoadingConv] = useState(false);

    useEffect(() => {
        localStorage.setItem('chat_isOpen', isOpen);
    }, [isOpen]);

    // Al cambiar de usuario (login/logout), limpiamos el estado en memoria — las conversaciones
    // viven en el servidor, escopadas por el JWT.
    useEffect(() => {
        setActiveConv(null);
        setMessages([]);
        setConversaciones([]);
    }, [user?.id]);

    const cargarConversaciones = async () => {
        try {
            const { data } = await axios.get(`${URL_ASISTENTE_IA}/conversaciones`, authHeaders());
            setConversaciones(data.data || []);
        } catch (e) {
            console.error('Error cargando conversaciones:', e);
        }
    };

    const seleccionarConversacion = async (id) => {
        setLoadingConv(true);
        try {
            const { data } = await axios.get(`${URL_ASISTENTE_IA}/conversaciones/${id}`, authHeaders());
            setActiveConv({ _id: data.data._id, titulo: data.data.titulo });
            setMessages(data.data.mensajes || []);
        } catch (e) {
            console.error('Error cargando conversación:', e);
        } finally {
            setLoadingConv(false);
        }
    };

    const nuevaConversacion = () => {
        setActiveConv(null);
        setMessages([]);
    };

    const eliminarConversacion = async (id) => {
        try {
            await axios.delete(`${URL_ASISTENTE_IA}/conversaciones/${id}`, authHeaders());
            setConversaciones(prev => prev.filter(c => c._id !== id));
            if (activeConv?._id === id) nuevaConversacion();
        } catch (e) {
            console.error('Error eliminando conversación:', e);
        }
    };

    const enviar = async (texto) => {
        const userMsg = { role: 'user', text: texto };
        setMessages(prev => [...prev, userMsg]);
        setIsLoading(true);

        try {
            const { data } = await axios.post(`${URL_ASISTENTE_IA}/ask`, {
                mensaje: texto,
                id_conversacion: activeConv?._id || null
            }, authHeaders());

            const respuesta = data.data;
            const botMsg = {
                role: 'assistant',
                text: respuesta.text || (typeof respuesta === 'string' ? respuesta : JSON.stringify(respuesta)),
                chart: respuesta.chart || null,
                rawData: respuesta.rawData || null
            };
            setMessages(prev => [...prev, botMsg]);

            if (!activeConv) {
                setActiveConv({ _id: respuesta.id_conversacion, titulo: respuesta.titulo });
                setConversaciones(prev => [{ _id: respuesta.id_conversacion, titulo: respuesta.titulo, updatedAt: new Date().toISOString() }, ...prev]);
            } else {
                setConversaciones(prev => prev.map(c => c._id === activeConv._id ? { ...c, updatedAt: new Date().toISOString() } : c));
            }
        } catch (error) {
            console.error("Error en Chat AI:", error);
            const errorMsg = error.response?.status === 403
                ? "No tienes permisos para usar esta función."
                : (error.response?.data?.message || "Lo siento, tuve un problema conectando con el servidor.");
            setMessages(prev => [...prev, { role: 'assistant', text: errorMsg, error: true }]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ChatContext.Provider value={{
            isOpen,
            setIsOpen,
            conversaciones,
            activeConv,
            messages,
            isLoading,
            loadingConv,
            cargarConversaciones,
            seleccionarConversacion,
            nuevaConversacion,
            eliminarConversacion,
            enviar
        }}>
            {children}
        </ChatContext.Provider>
    );
};
